import http from './httpServices';

/**
 * Get single recording by ID
 */
export const getRecording = async (recordingId) => {
  return http.get(`/recording/${recordingId}`);
};

/**
 * Get recording by share token (public access)
 */
export const getRecordingByShareToken = async (shareToken) => {
  return http.get(`/recording/share/${shareToken}`);
};

/**
 * Get transcript by share token (public access)
 */
export const getTranscriptByShareToken = async (shareToken) => {
  return http.get(`/recording/share/${shareToken}/transcript`);
};

/**
 * Get notes by share token (public access, from S3)
 */
export const getNotesByShareToken = async (shareToken) => {
  return http.get(`/recording/share/${shareToken}/notes`);
};

/**
 * Get recordings for authenticated user
 */
export const getUserRecordings = async (page = 1, limit = 20) => {
  return http.get(`/recording/user/list?page=${page}&limit=${limit}`);
};

/**
 * Get all recordings in a session group
 */
export const getSessionGroupRecordings = async (sessionGroupId) => {
  return http.get(`/recording/session-group/${sessionGroupId}`);
};

/**
 * Update share settings for a recording (legacy single-share endpoint)
 */
export const updateShareSettings = async (recordingId, settings) => {
  return http.put(`/recording/${recordingId}/share`, settings);
};

// ======= Per-recipient Share Links =======

/** List all shares for a recording (plus the legacy single-share if any) */
export const listRecordingShares = async (recordingId) => {
  return http.get(`/recording/${recordingId}/shares`);
};

/** Create a new share link for a recording */
export const createRecordingShare = async (recordingId, payload) => {
  return http.post(`/recording/${recordingId}/shares`, payload);
};

/** Update an existing share link (label and/or settings) */
export const updateRecordingShare = async (shareId, payload) => {
  return http.put(`/recording/shares/${shareId}`, payload);
};

/** Revoke (delete) a share link */
export const deleteRecordingShare = async (shareId) => {
  return http.delete(`/recording/shares/${shareId}`);
};

/** Delete the legacy single-share token on a recording (manual revoke) */
export const deleteLegacyRecordingShare = async (recordingId) => {
  return http.delete(`/recording/${recordingId}/legacy-share`);
};

/**
 * Get summary status for polling
 */
export const getSummaryStatus = async (recordingId) => {
  return http.get(`/recording/${recordingId}/summary-status`);
};

/**
 * Get structured notes for a recording (fetched from S3)
 */
export const getRecordingNotes = async (recordingId) => {
  return http.get(`/recording/${recordingId}/notes`);
};

/**
 * Generate structured notes on-demand for a recording
 */
export const generateNotesOnDemand = async (recordingId) => {
  return http.post(`/recording/${recordingId}/generate-notes`);
};

/**
 * Get chat messages for a recording (fetched from S3)
 */
export const getRecordingChat = async (recordingId) => {
  return http.get(`/recording/${recordingId}/chat`);
};

/**
 * Get chat by share token (public access, from S3)
 */
export const getChatByShareToken = async (shareToken) => {
  return http.get(`/recording/share/${shareToken}/chat`);
};

/**
 * Delete a recording
 */
export const deleteRecording = async (recordingId) => {
  return http.delete(`/recording/${recordingId}`);
};

/**
 * Create a bookmark
 */
export const createBookmark = async (data) => {
  return http.post(`/bookmark`, data);
};

/**
 * Get bookmarks for a recording
 */
export const getBookmarks = async (recordingId) => {
  return http.get(`/bookmark/${recordingId}`);
};

/**
 * Delete a bookmark
 */
export const deleteBookmark = async (bookmarkId) => {
  return http.delete(`/bookmark/${bookmarkId}`);
};

// ======= Recording Folders =======

export const getRecordingFolders = async () => {
  return http.get('/recording/folders/list');
};

export const createRecordingFolder = async (name, color, parentId) => {
  return http.post('/recording/folders', { name, color, parentId: parentId || null });
};

export const updateRecordingFolder = async (folderId, data) => {
  return http.put(`/recording/folders/${folderId}`, data);
};

export const deleteRecordingFolder = async (folderId) => {
  return http.delete(`/recording/folders/${folderId}`);
};

export const moveRecordingsToFolder = async (recordingIds, folderId) => {
  return http.put('/recording/move-to-folder', { recordingIds, folderId });
};

// ======= Multi-Layout Recording Assets =======

/**
 * Get layout processing status for a recording
 */
export const getProcessingStatus = async (recordingId) => {
  return http.get(`/recording/${recordingId}/processing-status`);
};

/**
 * Get all recording assets (layout files) for a recording
 */
export const getRecordingAssets = async (recordingId) => {
  return http.get(`/recording/${recordingId}/assets`);
};

/**
 * Get download URL for a specific recording asset
 */
export const getAssetDownloadUrl = (recordingId, assetId) => {
  const token = localStorage.getItem('token');
  return `${process.env.REACT_APP_BASE_URL}/recording/${recordingId}/assets/${assetId}/download?token=${encodeURIComponent(token)}`;
};

/**
 * Public: list recording assets (all layouts) by share token
 */
export const getAssetsByShareToken = async (shareToken) => {
  return http.get(`/recording/share/${shareToken}/assets`);
};

/**
 * Public: download URL for an asset via share token (no auth required)
 */
export const getSharedAssetDownloadUrl = (shareToken, assetId) => {
  return `${process.env.REACT_APP_BASE_URL}/recording/share/${shareToken}/assets/${assetId}/download`;
};
